import * as v from "valibot";
import { ApiError } from "./http-transport";
import { appleSignIn, emailSignInStart, emailSignInVerify, googleCodeSignIn, googleSignIn } from "./standalone-auth";
import type { EmailSignInStartResponse, StandaloneSignInResponse } from "./standalone-auth.dto";

export const SIGN_IN_CODE_LENGTH = 6;
export const SIGN_IN_RESEND_SECONDS = 30;
export const SIGN_IN_INVALID_EMAIL_MESSAGE = "Enter a valid email address.";

export type SignInResult = StandaloneSignInResponse;

export type AppleSignInCredential = {
  identityToken: string;
  authorizationCode?: string;
  fullName?: { givenName?: string | null; familyName?: string | null };
};

export type SignInClientOptions = { baseUrl?: string };

export type StartEmailSignInOptions = SignInClientOptions & { next?: string };

const invokeOptions = (options?: SignInClientOptions) => ({ baseUrl: options?.baseUrl, skipAuth: true });

export const signInClient = {
  google: (idToken: string, options?: SignInClientOptions): Promise<SignInResult> => googleSignIn.invoke({ idToken }, invokeOptions(options)),
  googleCode: (code: string, redirectUri: string, options?: SignInClientOptions): Promise<SignInResult> =>
    googleCodeSignIn.invoke({ code, redirectUri }, invokeOptions(options)),
  apple: (credential: AppleSignInCredential, options?: SignInClientOptions): Promise<SignInResult> =>
    appleSignIn.invoke(
      { identityToken: credential.identityToken, authorizationCode: credential.authorizationCode, fullName: credential.fullName },
      invokeOptions(options),
    ),
  startEmail: (email: string, options?: StartEmailSignInOptions): Promise<EmailSignInStartResponse> =>
    emailSignInStart.invoke(options?.next === undefined ? { email } : { email, next: options.next }, invokeOptions(options)),
  verifyCode: (email: string, code: string, options?: SignInClientOptions): Promise<SignInResult> =>
    emailSignInVerify.invoke({ email, code }, invokeOptions(options)),
  verifyLink: (token: string, options?: SignInClientOptions): Promise<SignInResult> => emailSignInVerify.invoke({ token }, invokeOptions(options)),
};

const signInEmailSchema = v.pipe(v.string(), v.maxLength(254), v.email());

export const parseSignInEmail = (input: string): string | null => {
  const result = v.safeParse(signInEmailSchema, input.trim().toLowerCase());
  return result.success ? result.output : null;
};

export const normalizeSignInCode = (input: string): string => input.replace(/\D/g, "").slice(0, SIGN_IN_CODE_LENGTH);

export const isSignInCodeComplete = (code: string): boolean => /^\d+$/.test(code) && code.length === SIGN_IN_CODE_LENGTH;

const TOO_MANY_ATTEMPTS_MESSAGE = "Too many attempts. Try again later.";
const WRONG_CODE_MESSAGE = "That code is not right or has expired.";
const INVALID_LINK_MESSAGE = "This sign-in link is no longer valid.";
const GENERIC_FAILURE_MESSAGE = "Could not sign in. Please try again.";

const isClientError = (error: unknown): error is ApiError => error instanceof ApiError && error.status >= 400 && error.status < 500;

export const signInErrorMessage = (error: unknown, kind: "code" | "link" | "provider"): string => {
  if (error instanceof ApiError && error.status === 429) {
    return TOO_MANY_ATTEMPTS_MESSAGE;
  }
  if (kind === "link" && (error === undefined || isClientError(error))) {
    return INVALID_LINK_MESSAGE;
  }
  if (kind === "code" && isClientError(error)) {
    return WRONG_CODE_MESSAGE;
  }
  return GENERIC_FAILURE_MESSAGE;
};
